































































import { PropType } from 'vue';
import { mapActions, mapGetters } from 'vuex';
import { difference, unionWith } from 'lodash';
import { AxiosError } from 'axios';

import state from '@/mixins/state';

import {
  Configuration,
  ConfigurationItem,
  IConfigurationItem,
  IItemCategoryMapData
} from '@/models/configurator/configuration';

import { IItem } from '@/models';
import {
  AccessoryCategory,
  IAccessoryCategory,
  Module,
  ModuleAccessoryTypeEnum
} from '@/models/configurator/module';
import { ErrorManager } from '@/models/error';
import { cloneDeep } from 'lodash';
import ErrorAlert from '@/components/common/ErrorAlert.vue';

type CategoryConfigurationItem = ConfigurationItem & { category?: string };

export default state.extend({
  components: { ErrorAlert },
  name: 'ItemAccessoryBuilderCard',
  props: {
    configuration: {
      type: Object as PropType<Configuration>
    },

    module: {
      type: Object as PropType<Module>
    },

    snapshot: {
      type: Number as PropType<number>
    },

    exchangeRate: Number,
    currencyCode: String
  },

  data: () => ({
    search: '',
    configurationItems: [] as IConfigurationItem[],
    remoteConfigurationItems: [] as IConfigurationItem[],
    isLoading: false,
    errorMessage: '',
    detailErrorMessage: ''
  }),

  computed: {
    ...mapGetters({
      accessoryCategories: 'configuratorModule/getModuleAccessoryCategories'
    }),

    errorOccurred(): boolean {
      return this.errorMessage.length > 0;
    },

    isModuleConfigurable(): boolean {
      return this.module.accessory_type == ModuleAccessoryTypeEnum.Configurable;
    },

    /** Parsed accessory items into a readable list for table component */
    configurableAccessoryItems(): IConfigurationItem[] {
      return (this.accessoryCategories as IAccessoryCategory[]).reduce(
        (
          categoryConfigurableList: IConfigurationItem[],
          accessoryCategory: IAccessoryCategory
        ) => {
          try {
            const parsedConfigurationItemList = accessoryCategory.items.map(
              item => {
                const parsedConfigurableItem: IConfigurationItem = new ConfigurationItem(
                  {
                    item: item.item,
                    quantity: 0,
                    unit: item.unit,
                    description: item.description,
                    configuration: this.configuration.id,
                    unit_price: Number(item.price),
                    price: 0,
                    is_machine: item.is_machine,
                    accessory_category: {
                      id: accessoryCategory.id,
                      name: accessoryCategory.name
                    }
                  }
                );
                return parsedConfigurableItem;
              }
            );

            // for each item in each accessory add configuration item with accessory category attached
            categoryConfigurableList.push(...parsedConfigurationItemList);
          } catch (error) {
            this.errorMessage = ErrorManager.extractApiError(
              error as AxiosError
            );
          }
          return categoryConfigurableList;
        },
        []
      );
    }
  },

  async mounted() {
    if (this.isModuleConfigurable) {
      await this.initConfigurableModuleAccessories();
    } else {
      this.setComponentState('loading');
      await this.getItemCategoryMapfromJSON().then(async localList => {
        // Set local list of ids
        const localConfigurationItemsIdsList = localList.map(
          itemcategory => itemcategory.id
        );

        const remoteConfigurationItemsIdsList = await this.remoteConfigurationItemIds();

        const remoteConfigurationListDifference = difference(
          localConfigurationItemsIdsList,
          remoteConfigurationItemsIdsList
        );

        // Make a list of configuration item promise
        const configurationItemPromiseList: Promise<
          CategoryConfigurationItem
        >[] = remoteConfigurationListDifference.map(async id => {
          return {
            ...(await this.buildConfigurationItemFromCsiItem(id)),
            category: localList.find(item => item.id === id)?.category ?? ''
          };
        });

        // Promise to await from a fulfilled list of configurationItems
        // and set items
        Promise.all(configurationItemPromiseList)
          .then(result => {
            this.getConfigurationItems()
              .then(remoteConfigurationItemList => {
                // mew list of array is the remote items plus the diffrence between the remote and local items
                this.configurationItems = remoteConfigurationItemList.concat(
                  result
                );
              })
              .finally(() => {
                this.setComponentState('loaded');
              });
          })
          .catch(error => {
            this.errorMessage = ErrorManager.extractApiError(error);
            this.setComponentState('error', error);
          });
      });
    }
  },

  beforeDestroy() {
    this.configurationItems = [];
    this.remoteConfigurationItems = [];
  },

  methods: {
    ...mapActions({
      fetchCsiItem: 'csiItem/fetchCsiItem',

      createConfigurationItem:
        'configuratorConfiguration/createConfigurationItem',

      fetchConfigurationItems:
        'configuratorConfiguration/fetchConfigurationItems',

      updateConfigurationItem:
        'configuratorConfiguration/updateConfigurationItem',

      deleteConfigurationItem:
        'configuratorConfiguration/deleteConfigurationItem',

      fetchEmployeeAccessoryCategories:
        'configuratorModule/fetchEmployeeAccessoryCategories'
    }),

    clearAllErrors(): void {
      this.errorMessage = '';
      this.detailErrorMessage = '';
    },

    async initConfigurableModuleAccessories() {
      this.setComponentState('loading');
      try {
        // get configuration item list from api

        const savedConfigurationItemList = await this.fetchConfigurationItems(
          this.configuration.id!
        );

        // NOT assign value saved configuration items by reference to local variable
        this.remoteConfigurationItems = [...savedConfigurationItemList];

        // Get configurable module accessory items list from api
        await this.fetchEmployeeAccessoryCategories(this.module.id);

        // Combine saved items and items from module configurable accessory and override accessory items with saved items when category and item key match
        const configurationItemJoinedList = unionWith(
          this.remoteConfigurationItems,
          this.configurableAccessoryItems,
          (serverItem, accessoryItem) => {
            return (
              accessoryItem.item === serverItem.item &&
              (accessoryItem.accessory_category as AccessoryCategory).id ===
                (serverItem.accessory_category as AccessoryCategory).id
            );
          }
        );

        // Set current table configuration item list with new joined list
        this.configurationItems = configurationItemJoinedList;

        this.setComponentState('loaded');
      } catch (err) {
        const error = err as AxiosError;
        if (error.response) {
          if (error.response.data.detail) {
            this.detailErrorMessage = error.response.data.detail;
          }
        }
        this.errorMessage = ErrorManager.extractApiError(error);
        this.setComponentState('error', error);
      }
    },

    async getItemCategoryAccessoryList(
      moduleName: string
    ): Promise<IItemCategoryMapData[]> {
      const accessoryTypesKeyWords = [
        'Jamesway Platinum 2.0 Accessories',
        'Jamesway Multi Stage Accessories',
        'Chick Master Accessories',
        'Platinum 3 Accessories CM-Series',
        'Platinum 3 Accessories P-Series'
      ];
      let accessoryType = '';

      accessoryTypesKeyWords.forEach(word => {
        if (word.toLowerCase() == moduleName.toLowerCase()) {
          switch (word) {
            case 'Jamesway Platinum 2.0 Accessories':
              accessoryType = 'platinum';
              return;

            case 'Chick Master Accessories':
              accessoryType = 'chickmaster';
              return;

            case 'Jamesway Multi Stage Accessories':
              accessoryType = 'multistage';
              return;

            case 'Platinum 3 Accessories P-Series':
              accessoryType = 'platinum_3_accessories_p_series';
              return;

            case 'Platinum 3 Accessories CM-Series':
              accessoryType = 'platinum_3_accessories_cm_series';
              return;

            default:
              new Error('Type of accessory not provided');
              return;
          }
        }
      });

      const { default: accessories } = await import(
        `@/data/accessories/${accessoryType}_accessories.json`
      );

      return accessories;
    },

    async getItemCategoryMapfromJSON(): Promise<IItemCategoryMapData[]> {
      let accessories: IItemCategoryMapData[] = [];
      // https://eslint.org/docs/rules/no-unsafe-finally
      try {
        const moduleName = this.module.name?.toLowerCase();

        if (moduleName) {
          accessories = await this.getItemCategoryAccessoryList(moduleName);
        }
        return accessories;
      } catch (error) {
        this.errorMessage = ErrorManager.extractApiError(error as AxiosError);
        return accessories;
      }
    },

    async buildConfigurationItemFromCsiItem(id: string) {
      return this.parseConfigurationItem(
        await this.fetchCsiItem({
          id: id,
          params: this.buildCsiItemQueryParams()
        })
      );
    },

    parseConfigurationItem(csiItem: IItem): ConfigurationItem {
      return new ConfigurationItem({
        item: csiItem.item,
        quantity: 0,
        unit: csiItem.unit,
        description: csiItem.description,
        configuration: this.configuration.id,
        unit_price: Number(csiItem.price),
        price: 0,
        is_machine: csiItem.is_machine
      });
    },

    /**
     * Fetch item from api based on prop configurations id
     * and set the @see remoteConfigurationItems
     *
     * @param {number} configurationId configuration id to search list by
     */
    async fetchItems(configurationId: number) {
      this.clearAllErrors();

      try {
        this.remoteConfigurationItems = await this.fetchConfigurationItems(
          configurationId
        );
      } catch (err) {
        const error = err as AxiosError;
        if (error.response) {
          if (error.response.data.detail) {
            this.detailErrorMessage = error.response.data.detail;
          }
        }
        this.errorMessage = ErrorManager.extractApiError(error);
      }
    },

    async getConfigurationItems(): Promise<CategoryConfigurationItem[]> {
      await this.fetchItems(this.configuration.id!);

      const itemCategoryMapList = await this.getItemCategoryMapfromJSON();

      const remoteListWithCategory = this.remoteConfigurationItems.map(
        element => {
          const itemCategoryMapCategory = itemCategoryMapList.find(
            ({ id }) => id == element.item
          );
          const category = itemCategoryMapCategory?.category || '';

          return {
            ...element,
            category: category
          };
        }
      );

      return remoteListWithCategory;
    },

    buildCsiItemQueryParams(): URLSearchParams {
      const params = new URLSearchParams();
      params.append('commercial-description', 'true');
      if (this.snapshot) {
        params.append('pricing', String(this.snapshot));
      }
      return params;
    },

    async destroyItemConfiguration(configItem: ConfigurationItem) {
      if (configItem.id) {
        await this.deleteConfigurationItem(configItem);
      }

      if (this.isModuleConfigurable) {
        const accessoryItemIndex = this.getAccessoryConfigurationItemIndex(
          configItem
        );
        try {
          // TODO - LH - 2024-07-02 - Check why `configurableAccessoryItems` index look up is being updated by reference
          const configurationItem = new ConfigurationItem({
            item: configItem.item,
            quantity: 0,
            unit: configItem.unit,
            description: configItem.description,
            configuration: this.configuration.id,
            unit_price: Number(configItem.unit_price),
            price: 0,
            is_machine: configItem.is_machine,
            accessory_category: {
              id: (configItem.accessory_category as IAccessoryCategory).id,
              name: (configItem.accessory_category as IAccessoryCategory).name
            }
          });

          // Place deleted item with accessory item from api
          this.setConfigurationItem(configurationItem, accessoryItemIndex);

          // Remove deleted item from saved list
          this.removeSavedConfigurationItem(configurationItem);
        } catch (error) {
          this.errorMessage = ErrorManager.extractApiError(error as AxiosError);
        }
      } else {
        const index = this.configurationItems.indexOf(configItem);

        const itemCategoryMapList = await this.getItemCategoryMapfromJSON();
        const category =
          itemCategoryMapList.find(
            itemcategory => itemcategory.id == configItem.item
          )?.category || '';
        this.$set(this.configurationItems, index, {
          ...this.parseConfigurationItem(
            await this.fetchCsiItem({
              id: configItem.item,
              params: this.buildCsiItemQueryParams()
            })
          ),
          category: category
        });
      }
    },

    async remoteConfigurationItemIds(): Promise<string[]> {
      return this.getConfigurationItems().then(configItemList => {
        const remoteList = configItemList.map(({ item }) => String(item));

        return remoteList;
      });
    },

    async createOrUpdateConfigurationItem(item: ConfigurationItem) {
      // Work-around - LF-2021- Need to delete description
      //  - to ensure accessory description is translatable
      const itemClone = cloneDeep(item);
      delete itemClone.description;

      // validate if configItem is already set then update is so and create for otherwise
      try {
        if (itemClone.id) {
          return await this.updateConfigurationItem(itemClone).then(
            response => {
              return response;
            }
          );
        } else {
          return await this.createConfigurationItem(itemClone).then(
            response => {
              return response;
            }
          );
        }
      } catch (err) {
        const error = err as AxiosError;
        if (error.response) {
          if (error.response.data.detail) {
            this.detailErrorMessage = error.response.data.detail;
          }
        }
        this.errorMessage = ErrorManager.extractApiError(error);
      }
    },

    updateItemConfiguration(configItem: ConfigurationItem) {
      this.clearAllErrors();

      // Cloning to avoid updating accessory_category in reference list view
      const payload = cloneDeep(configItem);

      // set accessory_category to id when creating or editing for configurable items
      if (this.isModuleConfigurable) {
        payload.accessory_category = (payload.accessory_category as IAccessoryCategory).id!;
      } else {
        // set 'accessory_category' to null for legacy accessory modules
        payload.accessory_category = null;
      }

      this.createOrUpdateConfigurationItem(payload).then(async response => {
        if (response) {
          if (this.isModuleConfigurable) {
            const accessoryItemIndex = this.getAccessoryConfigurationItemIndex(
              response
            );

            try {
              const configurationItem = new ConfigurationItem(response);

              // Update configuration list to use saved configuration item
              this.setConfigurationItem(configurationItem, accessoryItemIndex);

              // Update price list with newly saved configuration item
              this.addOrUpdateSavedConfigurationItem(configurationItem);
            } catch (error) {
              this.errorMessage = ErrorManager.extractApiError(
                error as AxiosError
              );
            }
          } else {
            const itemCategoryMapList = await this.getItemCategoryMapfromJSON();
            const itemCategoryMapCategory = itemCategoryMapList.find(
              ({ id }) => id == configItem.item
            )?.category;
            const category = itemCategoryMapCategory || '';
            this.setConfigurationItem(
              {
                ...response,
                category: category
              },
              this.configurationItems.indexOf(configItem)
            );
          }
        }
      });
    },

    setConfigurationItem(newItem: ConfigurationItem, index: number) {
      this.$set(this.configurationItems, index, newItem);
    },

    save() {
      this.$emit('save');
    },

    getAccessoryConfigurationItemIndex(
      configurationItem: IConfigurationItem
    ): number {
      return this.configurationItems.findIndex(item => {
        return (
          item.item === configurationItem.item &&
          (item.accessory_category as IAccessoryCategory).id! ===
            (configurationItem.accessory_category as IAccessoryCategory).id!
        );
      });
    },

    getSavedConfigurationItemIndex(
      configurationItem: IConfigurationItem
    ): number {
      return this.remoteConfigurationItems.findIndex(item => {
        return (
          item.item === configurationItem.item &&
          (item.accessory_category as IAccessoryCategory).id! ===
            (configurationItem.accessory_category as IAccessoryCategory).id!
        );
      });
    },

    addOrUpdateSavedConfigurationItem(configurationItem: IConfigurationItem) {
      const remoteItemIndex = this.getSavedConfigurationItemIndex(
        configurationItem
      );

      if (remoteItemIndex != -1) {
        this.$set(
          this.remoteConfigurationItems,
          remoteItemIndex,
          configurationItem
        );
      } else {
        this.remoteConfigurationItems.push(configurationItem);
      }
    },

    removeSavedConfigurationItem(configurationItem: IConfigurationItem) {
      const remoteItemIndex = this.getSavedConfigurationItemIndex(
        configurationItem
      );

      this.remoteConfigurationItems.splice(remoteItemIndex, 1);
    }
  }
});
